// ui-kit: hand-built
import { type ComponentPropsWithRef, type CSSProperties, type Ref } from 'react';

import { classNames } from '../../internal/classNames';

import Icon, { type IconSource } from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';

export type AvatarSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
export type AvatarVariant = 'light' | 'base';
export type AvatarColor =
    | 'grey'
    | 'yellow'
    | 'orange'
    | 'red'
    | 'cranberry'
    | 'pink'
    | 'purple'
    | 'blue'
    | 'green'
    | 'gradient';

type AvatarTypologyProps =
    | {
          text: string;
          icon?: never;
          image?: never;
      }
    | {
          text?: never;
          icon: IconSource;
          image?: never;
      }
    | {
          text?: never;
          icon?: never;
          image: string;
      };

export type AvatarProps = Omit<ComponentPropsWithRef<'div'>, 'color' | 'children'> &
    AvatarTypologyProps & {
        size?: AvatarSize;
        color?: AvatarColor;
        variant?: AvatarVariant;
        alt?: string;
        ref?: Ref<HTMLDivElement>;
    };

const ICON_SIZES: Record<AvatarSize, number> = {
    xsmall: 8,
    small: 12,
    medium: 16,
    large: 24,
    xlarge: 32,
    xxlarge: 48,
};

const RANDOM_COLORS: Exclude<AvatarColor, 'grey' | 'gradient'>[] = [
    'blue',
    'cranberry',
    'green',
    'orange',
    'pink',
    'purple',
    'red',
    'yellow',
];

/** Stable support color from the first character of `text` (Platform Avatar rule). */
export function getRandomColor(text: string): AvatarColor {
    if (!text) {
        return 'grey';
    }
    return RANDOM_COLORS[text.charCodeAt(0) % RANDOM_COLORS.length]!;
}

function avatarSurfaceStyle(color: AvatarColor, variant: AvatarVariant): CSSProperties {
    const surfaceName =
        color === 'gradient' && variant === 'base'
            ? 'support-gradient-image'
            : `support-${color}-${variant}`;

    const textColorName =
        variant === 'light'
            ? color === 'gradient'
                ? 'support-grey'
                : `support-${color}`
            : 'support-contrast';

    let iconColor: string;
    if (variant === 'base') {
        iconColor = 'var(--icon-support-contrast)';
    } else if (color === 'grey' || color === 'gradient') {
        iconColor = 'var(--icon-support-grey)';
    } else {
        iconColor = `var(--icon-support-${color}-base)`;
    }

    return {
        '--avatar-background': `var(--surface-${surfaceName})`,
        '--avatar-text-color': `var(--text-${textColorName})`,
        '--avatar-icon-color': iconColor,
    } as CSSProperties;
}

function Avatar({
    size = 'medium',
    color,
    variant = 'base',
    text,
    icon,
    image,
    alt = '',
    className = '',
    style,
    ref,
    ...rest
}: AvatarProps) {
    const avatarColor = color ?? (text !== undefined ? getRandomColor(text) : 'grey');
    const hasImage = image !== undefined;

    return (
        <div
            data-test-component='Avatar'
            {...rest}
            ref={ref}
            style={{ ...avatarSurfaceStyle(avatarColor, variant), ...style }}
            className={classNames({
                'ui-avatar': true,
                [`ui-avatar--size-${size}`]: true,
                'ui-avatar--image': hasImage,
                [className]: Boolean(className),
            })}>
            {text !== undefined && (
                <span
                    className={classNames({
                        'ui-avatar__text': true,
                        [`ui-avatar__text--size-${size}`]: true,
                    })}
                    data-test-name='text'>
                    {text}
                </span>
            )}

            {icon !== undefined && (
                <Icon
                    icon={icon}
                    size={ICON_SIZES[size]}
                    className='ui-avatar__icon'
                    data-test-name='icon'
                />
            )}

            {image !== undefined && (
                <span className='ui-avatar__image'>
                    <img
                        src={image}
                        alt={alt}
                        data-test-name='image'
                    />
                </span>
            )}
        </div>
    );
}

Avatar.displayName = 'Avatar';

export default Avatar;

export type AvatarGroupItem = {
    /** Shown in the hover tooltip (person, country, company, etc.). */
    label: string;
    color?: AvatarColor;
    variant?: AvatarVariant;
    text?: string;
    icon?: IconSource;
    image?: string;
};

export type AvatarGroupProps = Omit<ComponentPropsWithRef<'ul'>, 'children'> & {
    items: AvatarGroupItem[];
    size?: AvatarSize;
    /** Max avatars before the +N accumulator. Defaults to 3 (Figma). */
    max?: number;
    onItemClick?: (item: AvatarGroupItem, index: number) => void;
    onOverflowClick?: () => void;
    ref?: Ref<HTMLUListElement>;
};

const DEFAULT_GROUP_MAX = 3;

const SMALL_GROUP_SIZES: AvatarSize[] = ['xsmall', 'small', 'medium'];

function AvatarGroup({
    items,
    size = 'large',
    max = DEFAULT_GROUP_MAX,
    onItemClick,
    onOverflowClick,
    className = '',
    ref,
    ...rest
}: AvatarGroupProps) {
    const displayed = items.slice(0, max);
    const overflow = items.length - max;
    const isCompact = SMALL_GROUP_SIZES.includes(size);

    return (
        <ul
            {...rest}
            ref={ref}
            className={classNames({
                'ui-avatar-group': true,
                'ui-avatar-group--compact': isCompact,
                [className]: Boolean(className),
            })}
            data-test-component='AvatarGroup'>
            {displayed.map((item, index) => {
                const avatar = (
                    <Avatar
                        size={size}
                        color={item.color}
                        variant={item.variant ?? 'base'}
                        {...(item.image
                            ? { image: item.image, alt: item.label }
                            : item.icon
                              ? { icon: item.icon }
                              : { text: item.text ?? item.label.slice(0, 1) })}
                        className={classNames({
                            'ui-avatar-group__item': true,
                            'ui-avatar-group__item--clickable': Boolean(onItemClick),
                        })}
                        onClick={onItemClick ? () => onItemClick(item, index) : undefined}
                    />
                );

                return (
                    <li key={`avatar-group-${item.label}-${index}`}>
                        <Tooltip content={item.label}>{avatar}</Tooltip>
                    </li>
                );
            })}
            {overflow > 0 && (
                <li className='ui-avatar-group__overflow'>
                    <Avatar
                        text={`+${overflow}`}
                        color='grey'
                        variant='light'
                        size={size}
                        className={classNames({
                            'ui-avatar-group__item': true,
                            'ui-avatar-group__item--clickable': Boolean(onOverflowClick),
                        })}
                        onClick={onOverflowClick}
                    />
                </li>
            )}
        </ul>
    );
}

AvatarGroup.displayName = 'AvatarGroup';

export { AvatarGroup };
